import { Action, ActionReducer, createReducer } from '@ngrx/store';
import { onApiCall } from '../../../common/store/generic.reducer';
import { GenericState, IState } from '../../../common/store/types';
import { accountingActions } from './accounting.actions';
import {
  AccountModel,
  AccountMoveModel,
  AccountMovePeriodModel,
  CashJournalEntry,
  CashJournalModel,
  JournalAssignRecord,
  PaymentEntryModel,
  SlipResponseModel,
} from './types';

export interface IAccountingState extends IState<AccountMoveModel> {
  getById: GenericState<AccountMoveModel>;
  getByContactId: GenericState<AccountMoveModel[]>;
  getByJournalId: GenericState<AccountMoveModel[]>;
  getUnbindedByContactId: GenericState<AccountMoveModel[]>;
  getUnbindedByContractId: GenericState<AccountMoveModel[]>;
  getUnbindedByInsuranceClaimId: GenericState<AccountMoveModel[]>;
  getUnbindedRepayment: GenericState<AccountMoveModel[]>;
  getUnbindedRepaymentByInsuranceClaim: GenericState<AccountMoveModel[]>;
  getUnbindedSlip: GenericState<AccountMoveModel[]>;
  createInstance: GenericState<AccountMoveModel>;
  add: GenericState<AccountMoveModel>;
  bind: GenericState<boolean>;
  getPeriods: GenericState<AccountMovePeriodModel[]>;
  importSlip: GenericState<AccountMoveModel>;
  changeAccountMoveStatus: GenericState<AccountMoveModel>;
  uploadProof: GenericState<boolean>;
  getAccountMovePdf: GenericState<any>;
  getBindedAccountMoveByAccountMoveId: GenericState<AccountMoveModel>;
  delete: GenericState<boolean>;
  unbind: GenericState<boolean>;
  getAccountByContactId: GenericState<AccountModel>;
  getAccountById: GenericState<AccountModel>;
  getCashJournal: GenericState<CashJournalModel>;
  addAndBind: GenericState<AccountMoveModel>;
  update: GenericState<AccountMoveModel>;
  getPayments: GenericState<PaymentEntryModel[]>;
  addInvoiceExtended: GenericState<number>;
}

export const accountingInitialState: IAccountingState = {
  getById: new GenericState<AccountMoveModel>(),
  getByContactId: new GenericState<AccountMoveModel[]>(),
  getByJournalId: new GenericState<AccountMoveModel[]>(),
  getUnbindedByContactId: new GenericState<AccountMoveModel[]>(),
  getUnbindedByContractId: new GenericState<AccountMoveModel[]>(),
  getUnbindedByInsuranceClaimId: new GenericState<AccountMoveModel[]>(),
  getUnbindedRepayment: new GenericState<AccountMoveModel[]>(),
  getUnbindedRepaymentByInsuranceClaim: new GenericState<AccountMoveModel[]>(),
  getUnbindedSlip: new GenericState<AccountMoveModel[]>(),
  createInstance: new GenericState<AccountMoveModel>(),
  add: new GenericState<AccountMoveModel>(),
  bind: new GenericState<boolean>(),
  getPeriods: new GenericState<AccountMovePeriodModel[]>(),
  importSlip: new GenericState<AccountMoveModel>(),
  changeAccountMoveStatus: new GenericState<AccountMoveModel>(),
  uploadProof: new GenericState<boolean>(),
  getAccountMovePdf: new GenericState<any>(),
  getBindedAccountMoveByAccountMoveId: new GenericState<AccountMoveModel>(),
  delete: new GenericState<boolean>(),
  unbind: new GenericState<boolean>(),
  getAccountByContactId: new GenericState<AccountModel>(),
  getAccountById: new GenericState<AccountModel>(),
  getCashJournal: new GenericState<CashJournalModel>(),
  addAndBind: new GenericState<AccountMoveModel>(),
  update: new GenericState<AccountMoveModel>(),
  getPayments: new GenericState<PaymentEntryModel[]>(),
  addInvoiceExtended: new GenericState<number>(),
};

const accountingReducers = [
  ...onApiCall<AccountMoveModel>(accountingActions.getById, 'getById'),
  ...onApiCall<AccountMoveModel[]>(
    accountingActions.getByContactId,
    'getByContactId',
  ),
  ...onApiCall<AccountMoveModel[]>(
    accountingActions.getByJournalId,
    'getByJournalId',
  ),
  ...onApiCall<AccountMoveModel[]>(
    accountingActions.getUnbindedByContactId,
    'getUnbindedByContactId',
  ),
  ...onApiCall<AccountMoveModel[]>(
    accountingActions.getUnbindedByContractId,
    'getUnbindedByContractId',
  ),
  ...onApiCall<AccountMoveModel[]>(
    accountingActions.getUnbindedByInsuranceClaimId,
    'getUnbindedByInsuranceClaimId',
  ),
  ...onApiCall<AccountMoveModel[]>(
    accountingActions.getUnbindedRepayment,
    'getUnbindedRepayment',
  ),
  ...onApiCall<AccountMoveModel[]>(
    accountingActions.getUnbindedRepaymentByInsuranceClaim,
    'getUnbindedRepaymentByInsuranceClaim',
  ),
  ...onApiCall<JournalAssignRecord[]>(
    accountingActions.getUnbindedSlip,
    'getUnbindedSlip',
  ),
  ...onApiCall<AccountMoveModel>(
    accountingActions.createInstance,
    'createInstance',
  ),
  ...onApiCall<AccountMoveModel>(accountingActions.add, 'add'),
  ...onApiCall<boolean>(accountingActions.bind, 'bind'),
  ...onApiCall<AccountMovePeriodModel[]>(
    accountingActions.getPeriods,
    'getPeriods',
  ),
  ...onApiCall<SlipResponseModel[]>(accountingActions.importSlip, 'importSlip'),
  ...onApiCall<AccountMoveModel>(
    accountingActions.changeAccountMoveStatus,
    'changeAccountMoveStatus',
  ),
  ...onApiCall<boolean>(accountingActions.uploadProof, 'uploadProof'),
  ...onApiCall<any>(accountingActions.getAccountMovePdf, 'getAccountMovePdf'),
  ...onApiCall<any>(
    accountingActions.getBindedAccountMoveByAccountMoveId,
    'getBindedAccountMoveByAccountMoveId',
  ),
  ...onApiCall<boolean>(accountingActions.delete, 'delete'),
  ...onApiCall<boolean>(accountingActions.unbind, 'unbind'),
  ...onApiCall<AccountModel>(
    accountingActions.getAccountByContactId,
    'getAccountByContactId',
  ),
  ...onApiCall<AccountModel>(
    accountingActions.getAccountById,
    'getAccountById',
  ),
  ...onApiCall<CashJournalModel>(
    accountingActions.getCashJournal,
    'getCashJournal',
  ),
  ...onApiCall<AccountMoveModel>(accountingActions.addAndBind, 'addAndBind'),
  ...onApiCall<AccountMoveModel>(accountingActions.update, 'update'),
  ...onApiCall<PaymentEntryModel[]>(
    accountingActions.getPayments,
    'getPayments',
  ),
  ...onApiCall<number>(
    accountingActions.addInvoiceExtended,
    'addInvoiceExtended',
  ),
];

export const accountingReducer = createReducer(
  accountingInitialState,
  ...accountingReducers,
) as ActionReducer<IAccountingState, Action>;

export function useReducerFunc(state: any | undefined, action: Action) {
  return accountingReducer(state, action);
}
